/**
 * Reads the first message out of a DRF error body: a `detail` string, or the first message of a
 * list or of a field's list.
 */
function errorMessage(data) {
    const first = (value) => (Array.isArray(value) ? value[0] : value);

    if (!data || typeof data !== 'object') return null;
    if (data.detail) return first(data.detail);
    if (Array.isArray(data)) return first(data);

    const field = Object.values(data)[0];
    return field ? first(field) : null;
}

/**
 * Calls one of GDDash's endpoints that read a Search Console API directly — URL Inspection,
 * Sitemaps — and returns its JSON.
 *
 * Those endpoints borrow the OAuth credentials linked to a domain, so every call names a
 * credentials domain alongside the property; the Search Analytics reports go through the
 * gsc-test-query proxy in ./query instead.
 *
 * A refusal throws with the server's message. A spent quota is marked on the error (`quota`),
 * because a report treats it differently from every other failure: what is already on screen
 * stays, labelled as not refreshed, instead of being replaced by an error.
 */
export async function requestGscApi(path, { method = 'GET', params, body } = {}) {
    const url = params ? `${path}?${new URLSearchParams(params)}` : path;

    const response = await fetch(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': window.csrf_token,
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
        const error = new Error(errorMessage(data) ?? response.statusText);
        error.quota = response.status === 429;
        throw error;
    }

    return data;
}
